import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, useWindowDimensions } from 'react-native';
import { ON_ACCENT_TEXT } from '../styles/accentText';
import Ionicons from '@expo/vector-icons/Ionicons';
import { colors } from '../styles/colors';
import { loadCookieConsent, peekCookieConsent, setCookieConsent, subscribeCookieConsent } from '../services/cookieConsent';
import { marketingTags } from '../services/marketingTagRuntime';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n';
import { setCookieBannerInset } from './cookieBannerInset';
import { ALL_COOKIES, NECESSARY_COOKIES_ONLY } from '../services/cookieChoice';
import { navigateTo } from '../navigation/rootNavigation';
import { openCookiePreferences } from './cookiePreferences';

/**
 * CORE-1435 — уже этой ширины текст и кнопки в одну строку не помещаются: колонка
 * текста сжималась до ~100px, карточка вырастала до половины экрана и ложилась
 * на кнопку «Присоединиться» Live-страницы. На узком экране кнопки уходят под
 * текст, и карточка остаётся низкой.
 */
const COMPACT_BELOW_PX = 600;

/**
 * CORE-1142 — согласие на cookie сторонних счётчиков.
 *
 * CORE-1659 — ответ даётся по категориям: «Принять все», «Только необходимые»
 * или «Настроить» (окно `CookiePreferencesModal` с выбором по каждой). Баннер
 * остаётся на экране, пока окно открыто: закрыть его без ответа — не ответ.
 *
 * CORE-1775 — кнопки «Принять» / «Оставить только необходимые» / «Отказаться»;
 * «Настроить» — ссылка в тексте рядом с Политикой. «Отказаться» и «только
 * необходимые» записывают один и тот же выбор: Метрика в необходимых и грузится
 * при любом ответе, а отключаемые категории выключены в обоих случаях.
 *
 * Компонент существовал и раньше, но не рендерился нигде: согласия у людей не
 * спрашивали, а записанное значение никто не читал. Теперь он смонтирован в
 * `AppContent`, а его ответ — единственный гейт перед загрузкой внешних
 * счётчиков (`marketingTagRuntime`).
 *
 * Спрашиваем ТОЛЬКО там, где есть что спрашивать: если внешний слой не настроен
 * или это не то окружение, где он включается, никаких сторонних cookie не
 * появляется — и просить согласие на то, чего мы не делаем, значит приучать
 * закрывать баннер не читая.
 */
export const CookieBanner: React.FC = () => {
  const { colors: themeColors } = useTheme();
  const { t } = useI18n('common');
  const [visible, setVisible] = useState(false);
  const { width } = useWindowDimensions();
  const compact = width < COMPACT_BELOW_PX;

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!marketingTags.isEligible()) return;
    let cancelled = false;
    // CORE-1435 — решение сверяем с текущим значением, а не с тем, с которым
    // разрешилось чтение: ответ мог прийти раньше (с сервера после входа).
    loadCookieConsent().then(() => {
      if (!cancelled && !peekCookieConsent()) setVisible(true);
    });
    // Ответ, данный не в баннере (Настройки, сервер после входа), тоже его
    // закрывает — иначе баннер висел бы над уже решённым вопросом.
    const unsubscribe = subscribeCookieConsent((consent) => {
      if (consent) setVisible(false);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  // CORE-1435 — пока баннер на экране, его высоту знает `cookieBannerInset`
  // (пишется в onLayout); скрылся или размонтирован — место освобождается.
  useEffect(() => {
    if (!visible) return;
    return () => setCookieBannerInset(0);
  }, [visible]);

  if (!visible) return null;

  const handleAccept = () => {
    void setCookieConsent(ALL_COOKIES);
    setVisible(false);
  };

  const handleNecessaryOnly = () => {
    void setCookieConsent(NECESSARY_COOKIES_ONLY);
    setVisible(false);
  };

  return (
    <View
      style={[styles.container, compact && styles.containerCompact]}
      onLayout={(e) => setCookieBannerInset(e.nativeEvent.layout.height)}
    >
      <View
        style={[
          styles.content,
          compact && styles.contentCompact,
          { backgroundColor: themeColors.canvas, borderColor: themeColors.border },
        ]}
      >
        <View style={styles.textSection}>
          <View style={styles.titleRow}>
            <Ionicons name="shield-checkmark-outline" size={18} color={themeColors.textPrimary} />
            <Text style={[styles.title, { color: themeColors.textPrimary }]}>{t('cookies.title')}</Text>
          </View>
          <Text style={[styles.description, { color: themeColors.textSecondary }]}>
            {t('cookies.description')}{' '}
            <Text
              testID="cookie-banner-policy"
              accessibilityRole="link"
              style={[styles.link, { color: themeColors.textPrimary }]}
              onPress={() => navigateTo('LegalDoc', { docId: 'privacy-policy' })}
            >
              {t('cookies.policyLink')}
            </Text>
            {' · '}
            <Text
              testID="cookie-banner-customize"
              accessibilityRole="link"
              style={[styles.link, { color: themeColors.textPrimary }]}
              onPress={openCookiePreferences}
            >
              {t('cookies.customize')}
            </Text>
          </Text>
        </View>
        <View style={[styles.buttonRow, compact && styles.buttonRowCompact]}>
          <TouchableOpacity
            testID="cookie-banner-decline"
            style={[styles.declineButton, compact && styles.buttonCompact, { borderColor: themeColors.border, backgroundColor: themeColors.canvas }]}
            onPress={handleNecessaryOnly}
          >
            <Text style={[styles.declineButtonText, { color: themeColors.textSecondary }]}>{t('cookies.decline')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            testID="cookie-banner-necessary"
            style={[styles.declineButton, compact && styles.buttonCompact, { borderColor: themeColors.border, backgroundColor: themeColors.canvas }]}
            onPress={handleNecessaryOnly}
          >
            <Text style={[styles.declineButtonText, { color: themeColors.textSecondary }]}>{t('cookies.necessaryOnly')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            testID="cookie-banner-accept"
            style={[styles.acceptButton, compact && styles.buttonCompact]}
            onPress={handleAccept}
          >
            <Text style={styles.acceptButtonText}>{t('cookies.acceptAll')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 40,
    padding: 16,
    // CORE-1435 — отступ вокруг карточки прозрачный, и тапы в нём принадлежат
    // странице под баннером, а не баннеру.
    pointerEvents: 'box-none',
    ...(Platform.OS === 'web' && {
      position: 'fixed' as any,
    }),
  } as any,
  containerCompact: {
    padding: 10,
  },
  content: {
    maxWidth: 600,
    marginHorizontal: 'auto',
    width: '100%',
    borderRadius: 16,
    padding: 20,
    // CORE-1659 — кнопок три, рядом с текстом они в 600px не помещаются: всегда под ним.
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 14,
    borderWidth: 1,
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.08)',
    }),
  } as any,
  contentCompact: {
    padding: 14,
    gap: 12,
    borderRadius: 14,
  },
  textSection: {
    gap: 6,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  title: {
    fontSize: 14,
    fontWeight: '700',
  },
  description: {
    fontSize: 13,
    lineHeight: 18,
  },
  link: {
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: 8,
  },
  // На телефоне три кнопки в строку не читаются — в столбик, главная сверху.
  buttonRowCompact: {
    flexDirection: 'column-reverse',
  },
  declineButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
  },
  declineButtonText: {
    fontSize: 13,
    fontWeight: '600',
  },
  acceptButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: colors.orange600,
  },
  buttonCompact: {
    alignItems: 'center',
  },
  acceptButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: ON_ACCENT_TEXT,
  },
});
